import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// Escala del torso por fase: se expande al inhalar, se mantiene y se contrae al exhalar.
const TORSO_SCALE = { inhale: 1.08, hold: 1.08, exhale: 0.94, holdOut: 0.94 };
const SHOULDER_LIFT = { inhale: -3, hold: -3, exhale: 2, holdOut: 2 };

/**
 * OTRIX Play — figura humana sentada que respira al compás de la pauta: el torso escala y
 * los hombros suben/bajan con una transition cuya duración es EXACTAMENTE la de la fase
 * (misma técnica que la bola clásica, pero con una persona meditando). SVG propio, solo
 * transform/opacity; el contador de la fase vive en el centro del halo.
 */
function BreathingFigure({ phase = null }) {
  const theme = useTheme();
  const gold = theme.palette.gold.main;
  const goldSoft = theme.palette.gold.soft;
  const ink = theme.palette.text.primary;

  const active = phase?.phase || 'inhale';
  const seconds = phase?.total || 4;
  const scale = TORSO_SCALE[active] ?? 1;
  const lift = SHOULDER_LIFT[active] ?? 0;
  const ease = 'cubic-bezier(0.32, 0.72, 0, 1)';

  return (
    <Box sx={{ position: 'relative', width: 220, height: 220 }}>
      {/* Halo que respira con el torso */}
      <Box
        sx={{
          position: 'absolute',
          inset: 14,
          borderRadius: '50%',
          border: '1px solid',
          borderColor: 'gold.main',
          backgroundColor: goldSoft,
          transform: `scale(${scale})`,
          transition: `transform ${seconds}s ${ease}`
        }}
      />

      {/* Persona sentada (loto): cabeza fija, torso/hombros animados, piernas fijas. */}
      <Box
        component="svg"
        viewBox="0 0 120 120"
        aria-hidden
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        {/* Piernas cruzadas */}
        <path
          d="M28 88 q10 -12 32 -12 q22 0 32 12 q4 6 -4 8 l-56 0 q-8 -2 -4 -8 Z"
          fill={ink}
          opacity="0.28"
        />
        {/* Torso + hombros + brazos: grupo que respira */}
        <g
          style={{
            transform: `translateY(${lift}px) scale(${scale})`,
            transformOrigin: '60px 78px',
            transition: `transform ${seconds}s ${ease}`
          }}>
          <path
            d="M42 80 q-2 -26 18 -26 q20 0 18 26 q1 4 -4 5 l-28 0 q-5 -1 -4 -5 Z"
            fill={ink}
            opacity="0.55"
          />
          {/* Brazos hacia las rodillas */}
          <path d="M42 62 q-10 10 -8 22" stroke={ink} strokeOpacity="0.45" strokeWidth="4.5" strokeLinecap="round" fill="none" />
          <path d="M78 62 q10 10 8 22" stroke={ink} strokeOpacity="0.45" strokeWidth="4.5" strokeLinecap="round" fill="none" />
        </g>
        {/* Cabeza */}
        <circle cx="60" cy="42" r="10.5" fill={ink} opacity="0.65" />
      </Box>

      {/* Contador de la fase */}
      {phase && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            pt: 1.25
          }}>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ color: gold, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
            {phase.remaining}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

BreathingFigure.propTypes = {
  /** { phase: 'inhale'|'hold'|'exhale'|'holdOut', remaining, total } de phaseAt(). */
  phase: PropTypes.shape({
    phase: PropTypes.string,
    remaining: PropTypes.number,
    total: PropTypes.number
  })
};

export default BreathingFigure;
